import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { usePatients } from '../../hooks/usePatients';
import { api } from '../../lib/api';
import {
  X,
  Mail,
  MessageSquare,
  Lock,
  Sparkles,
  Send,
  AlertCircle,
} from 'lucide-react';

export default function SendMessageDrawer({ isOpen, onClose, patient }) {
  const { updatePatient } = usePatients();
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('email');
  const [loadingAI, setLoadingAI] = useState(false);
  const [sending, setSending] = useState(false);
  const [secureMessage, setSecureMessage] = useState(false);
  const [visible, setVisible] = useState(false);

  const hasEmail = !!patient?.email;
  const hasPhone = !!patient?.phone;
  const canSend = channel === 'email' ? hasEmail : hasPhone;

  // Animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen && patient) {
      const firstName = patient.firstName || patient.name?.split(' ')[0] || 'there';
      setMessage(
        `Hi ${firstName},\n\nJust checking in! Please let us know if you need help with your medication or have any questions.\n\nThanks,\nYour Care Team`
      );
      setSecureMessage(false);

      // Auto-select available channel
      if (!hasEmail && hasPhone) {
        setChannel('sms');
      } else {
        setChannel('email');
      }
    }
  }, [isOpen, patient, hasEmail, hasPhone]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !patient) return null;

  const generateAIMessage = async () => {
    setLoadingAI(true);
    try {
      const data = await api.generateMessage({
        patient_id: patient?.id,
        message_type: 'general',
        channel,
      });

      if (data?.message) {
        setMessage(data.message);
        toast.success('Message generated');
      } else {
        toast.error('AI did not return a message');
      }
    } catch {
      toast.error('Failed to generate message');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !canSend) return;

    setSending(true);

    try {
      if (secureMessage) {
        // Send as secure message - API expects 'content' not 'message'
        await api.sendSecureMessage({
          patient_id: patient.id,
          content: message,
          subject: 'Secure Message From Your Care Team',
          channel: 'email',
        });
      } else if (channel === 'sms') {
        // Send SMS - API looks up phone from patient record
        await api.sendSms({
          patient_id: patient?.id,
          message,
        });
      } else {
        // Send email - API looks up email from patient record
        await api.sendEmail({
          patient_id: patient?.id,
          subject: 'Checking In From Your Care Team',
          body: message,
          html: message.replace(/\n/g, '<br>'),
        });
      }

      const destination = channel === 'sms' ? patient?.phone : patient?.email;
      toast.success(`Sent to ${destination}`);

      await updatePatient(patient.id, {
        status: 'contacted',
        last_touch: new Date().toISOString(),
      });

      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-[70] transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-message-title"
        className={`fixed top-0 right-0 w-full sm:w-[480px] h-full bg-white shadow-xl z-[80] flex flex-col transition-transform duration-200 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 id="send-message-title" className="text-lg font-semibold text-slate-900">
              Send Message
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              To {patient.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Channel Tabs */}
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setChannel('email')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                channel === 'email'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Mail className="h-4 w-4" strokeWidth={1.5} />
              Email
              {!hasEmail && (
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              )}
            </button>
            <button
              onClick={() => { setChannel('sms'); setSecureMessage(false); }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                channel === 'sms'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
              SMS
              {!hasPhone && (
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!canSend ? (
            /* No contact info warning */
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                <AlertCircle className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-medium text-slate-900 mb-1">
                No {channel === 'email' ? 'email' : 'phone'} on file
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Add {channel === 'email' ? 'an email address' : 'a phone number'} in the patient details to send {channel === 'email' ? 'emails' : 'SMS'}.
              </p>
              <button
                onClick={onClose}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 underline underline-offset-2"
              >
                Go back to add contact info
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Destination indicator */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                {channel === 'email' ? (
                  <>
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                    <span>{patient.email}</span>
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
                    <span>{patient.phone}</span>
                  </>
                )}
              </div>

              {/* Message textarea */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Message
                  </label>
                  <button
                    onClick={generateAIMessage}
                    disabled={loadingAI}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full disabled:opacity-50 transition-colors"
                  >
                    <Sparkles className={`h-3.5 w-3.5 text-amber-500 ${loadingAI ? 'animate-spin' : ''}`} strokeWidth={2} />
                    {loadingAI ? 'Writing...' : 'Write for me'}
                  </button>
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={10}
                  className="w-full px-4 py-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-300 transition-colors"
                  placeholder="Write your message..."
                />
              </div>

              {/* Secure message toggle */}
              {channel === 'email' && (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50 cursor-pointer hover:bg-slate-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={secureMessage}
                    onChange={() => setSecureMessage(!secureMessage)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Send as secure message
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Patient must verify their date of birth to view
                    </p>
                  </div>
                </label>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {canSend && (
          <div className="px-6 pt-4 pb-8 border-t border-slate-100 bg-slate-50/50">
            <button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                message.trim() && !sending
                  ? 'bg-slate-900 text-white hover:bg-slate-800'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                  Send {channel === 'email' ? 'Email' : 'SMS'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
